/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — native, SQL-free grid filter/sort evaluator.
 *
 * This is the "application's native filtering/sorting JSON structure" the NLQ
 * engine targets. An AiQuery (filters + sorts) is applied entirely in memory to
 * an array of already-loaded rows — there is NO query generation, NO SQL, and
 * nothing the AI emits can reach the database. Row-level security and the
 * Common Routine remain the only data paths.
 *
 * Kept in lockstep with the operator set in src/lib/ai/types.ts and the
 * server-side validator in supabase/functions/csm-ai/index.ts.
 */

import type { AiFilter, AiQuery, AiSort } from './types';

function asString(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Numeric comparison when both sides parse as finite numbers; else locale string. */
function compareValues(a: unknown, b: unknown): number {
  const na = typeof a === 'number' ? a : Number(a);
  const nb = typeof b === 'number' ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return asString(a).localeCompare(asString(b), undefined, { numeric: true, sensitivity: 'base' });
}

function isEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  return String(v).trim() === '';
}

function matchFilter(row: Record<string, unknown>, f: AiFilter): boolean {
  const cell = row[f.column];
  const cellStr = asString(cell).toLowerCase();
  const valStr  = asString(f.value).toLowerCase();

  switch (f.op) {
    case 'eq':           return asString(cell).toLowerCase() === valStr;
    case 'neq':          return asString(cell).toLowerCase() !== valStr;
    case 'contains':     return cellStr.includes(valStr);
    case 'starts_with':  return cellStr.startsWith(valStr);
    case 'ends_with':    return cellStr.endsWith(valStr);
    case 'gt':           return compareValues(cell, f.value) > 0;
    case 'gte':          return compareValues(cell, f.value) >= 0;
    case 'lt':           return compareValues(cell, f.value) < 0;
    case 'lte':          return compareValues(cell, f.value) <= 0;
    case 'before':       return compareValues(cell, f.value) < 0;
    case 'after':        return compareValues(cell, f.value) > 0;
    case 'is_empty':     return isEmpty(cell);
    case 'is_not_empty': return !isEmpty(cell);
    case 'in': {
      const list = Array.isArray(f.value) ? f.value : [f.value];
      const set = new Set(list.map((x) => asString(x).toLowerCase()));
      // For array cells (e.g. role_codes) match if any element is in the set.
      if (Array.isArray(cell)) return cell.some((c) => set.has(asString(c).toLowerCase()));
      return set.has(cellStr);
    }
    case 'between':
      return compareValues(cell, f.value) >= 0 && compareValues(cell, f.value2) <= 0;
    default:
      return true;
  }
}

function applySorts<T extends Record<string, unknown>>(rows: T[], sorts: AiSort[]): T[] {
  if (!sorts.length) return rows;
  const out = [...rows];
  out.sort((a, b) => {
    for (const s of sorts) {
      const cmp = compareValues(a[s.column], b[s.column]);
      if (cmp !== 0) return s.dir === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
  return out;
}

/**
 * Apply an AiQuery to an in-memory row array. Pure: returns a new array, never
 * mutates the input. Unknown columns/operators are treated as no-ops (the
 * server already strips hallucinated ones, this is belt-and-suspenders).
 */
export function applyAiQuery<T>(rows: T[], query: AiQuery | null): T[] {
  if (!query) return rows;
  // Rows are treated as keyed records internally; callers pass concrete row
  // interfaces (e.g. UserRow) which need no index signature at the call site.
  const recs = rows as unknown as Record<string, unknown>[];
  const filtered = (query.filters ?? []).reduce<Record<string, unknown>[]>(
    (acc, f) => acc.filter((row) => matchFilter(row, f)),
    recs,
  );
  return applySorts(filtered, query.sorts ?? []) as unknown as T[];
}

/** Human-readable one-liner for a filter chip, e.g. `is_active = Y`. */
export function describeFilter(f: AiFilter): string {
  const label: Record<AiFilter['op'], string> = {
    eq: '=', neq: '≠', contains: 'contains', starts_with: 'starts with',
    ends_with: 'ends with', gt: '>', gte: '≥', lt: '<', lte: '≤',
    in: 'in', between: 'between', is_empty: 'is empty', is_not_empty: 'is not empty',
    before: 'before', after: 'after',
  };
  if (f.op === 'is_empty' || f.op === 'is_not_empty') return `${f.column} ${label[f.op]}`;
  if (f.op === 'between') return `${f.column} ${label[f.op]} ${asString(f.value)}–${asString(f.value2)}`;
  if (f.op === 'in') return `${f.column} ${label[f.op]} [${(Array.isArray(f.value) ? f.value : [f.value]).join(', ')}]`;
  return `${f.column} ${label[f.op]} ${asString(f.value)}`;
}
